import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  alpha,
  LinearProgress,
  Button
} from '@mui/material';
import { Icon } from '@iconify/react';
import { styled, useTheme } from '@mui/material/styles';
import Page from '../components/Page';
import { AnalyticsKPICard } from '../components/ocpi/analytics';
import { useList } from 'src/hooks/useFetch.hook';
import { Loader } from 'src/components/Loader';
import { formatOcpiCurrency } from 'src/utils/formatTime';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  backgroundImage:
    theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(245,247,250,0.92) 100%)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 20px 60px rgba(0,0,0,0.45)'
      : '0 2px 8px rgba(0,0,0,0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 25px 70px ${alpha(theme.palette.common.black, 0.65)}`
        : `0 8px 24px ${alpha(theme.palette.primary.main, 0.10)}`,
  },
}));

const OCPIDashboard = () => {
  const theme = useTheme();
  const [dashboardData, setDashboardData] = useState({
    kpis: {
      activePartners: 12,
      totalLocations: 45,
      monthlyRevenue: 125000,
      systemHealth: 98.5
    },
    partnerStatus: {
      online: 8,
      degraded: 3,
      offline: 1
    },
    syncOperations: {
      running: 2,
      completed: 156,
      failed: 3
    },
    recentActivity: [
      { id: 1, type: 'sync', partner: 'ChargePoint', status: 'completed', time: '2 min ago' },
      { id: 2, type: 'error', partner: 'EVgo', status: 'failed', time: '5 min ago' },
      { id: 3, type: 'sync', partner: 'Blink', status: 'running', time: '8 min ago' },
      { id: 4, type: 'partner', partner: 'Electrify America', status: 'online', time: '12 min ago' },
    ]
  });
  const { data: d, isFetching, refetch } = useList('dashboardData');

  // Mock data for trends (in real app, this would come from API)
  const trends = {
    activePartners: { value: '+12%', icon: 'eva:trending-up-fill', color: 'success' },
    monthlyRevenue: { value: '+8.5%', icon: 'eva:trending-up-fill', color: 'success' }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'sync': return 'eva:sync-fill';
      case 'error': return 'eva:alert-circle-fill';
      case 'partner': return 'eva:people-fill';
      default: return 'eva:info-fill';
    }
  };

  const getActivityColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'running': return 'info';
      case 'failed': return 'error';
      case 'online': return 'success';
      default: return 'default';
    }
  };

  return (
    <Page title="OCPI Dashboard">
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Box>
          {/* Header Section */}
          <Card
            sx={{
              p: 3,
              borderRadius: 2,
              mb: 3,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow:
                theme.palette.mode === 'dark'
                  ? '0 18px 45px rgba(0,0,0,0.6)'
                  : theme.shadows[3],
              backgroundImage:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(135deg, rgba(16,22,30,0.95) 0%, rgba(21,30,44,0.95) 100%)'
                  : 'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(247,249,252,0.92) 100%)'
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                background:
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                    : 'linear-gradient(135deg, #00d25b 0%, #00b84a 100%)',
                boxShadow:
                  theme.palette.mode === 'dark'
                    ? `0 10px 28px ${alpha(theme.palette.common.black, 0.65)}`
                    : `0 4px 12px ${alpha('#00d25b', 0.18)}`,
              }}
            >
              <Icon icon="eva:bar-chart-fill" width={28} height={28} style={{ color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.2 }}>
                OCPI Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.2 }}>
                Real-time monitoring and analytics for OCPI operations
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.8 }}>
                Monitor partner status, sync operations, and system performance
              </Typography>
            </Box>
          </Card>

          {/* KPI Cards Section */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Key Performance Indicators
            </Typography>
            <Button
                variant="outlined"
                startIcon={<Icon icon="eva:refresh-fill" />}
                onClick={() => refetch()}
                disabled={isFetching}
              >
                {isFetching ? 'Refreshing...' : 'Refresh'}
              </Button>
          </Stack>
         {isFetching ? <Loader variant='skeleton' skeletonCount={4} skeletonVariant='kpi' /> : 
         <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <AnalyticsKPICard
                title="Active Partners"
                value={d.kpis.activePartners}
                icon="eva:people-fill"
                color="primary"
                showTrend={true}
                trendValue={`+${d.trends.partners.change}%`}
                trendIcon={`eva:trending-${d.trends.partners.direction}-fill`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AnalyticsKPICard
                title="Total Locations"
                value={d.kpis.totalLocations}
                icon="eva:pin-fill"
                color="info"
                subtitle="across all partners"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AnalyticsKPICard
                title="Monthly Revenue"
                value={formatOcpiCurrency(d.kpis.monthlyRevenue)}
                icon="eva:credit-card-fill"
                color="success"
                isCurrency={true}
                showTrend={true}
                trendValue={`${d.trends.revenue.change}%`}
                trendIcon={`eva:trending-${d.trends.revenue.direction}-fill`}
                trendSubtitle="vs last month"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AnalyticsKPICard
                title="System Health"
                value={d.kpis.systemHealth}
                icon="eva:activity-fill"
                color="warning"
                unit="%"
                subtitle="uptime this month"
              />
            </Grid>
          </Grid>}

          {/* Status Monitor and Sync Operations - Two Column Layout */}
          {/* TODO: Status Monitor and Sync Operations - Two Column Layout (Coming Soon) */}
          {/*
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <StyledCard sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#212b36' }}>
                  Partner Status Monitor
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                        <Icon icon="eva:checkmark-circle-2-fill" width={20} height={20} style={{ color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Online
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Partners connected and active
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={dashboardData.partnerStatus.online} 
                      color="success" 
                      sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                        <Icon icon="eva:alert-triangle-fill" width={20} height={20} style={{ color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Degraded
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Partners with limited functionality
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={dashboardData.partnerStatus.degraded} 
                      color="warning" 
                      sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'error.lighter', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                        <Icon icon="eva:close-circle-fill" width={20} height={20} style={{ color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Offline
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Partners disconnected or inactive
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={dashboardData.partnerStatus.offline} 
                      color="error" 
                      sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  </Box>
                </Stack>
              </StyledCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledCard sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, color: '#212b36' }}>
                  Sync Operations
                </Typography>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                        <Icon icon="eva:sync-fill" width={20} height={20} style={{ color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Running
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Active sync operations
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={dashboardData.syncOperations.running} 
                      color="info" 
                      sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                        <Icon icon="eva:checkmark-circle-2-fill" width={20} height={20} style={{ color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Completed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Successfully finished syncs
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={dashboardData.syncOperations.completed} 
                      color="success" 
                      sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, bgcolor: 'error.lighter', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>
                        <Icon icon="eva:close-circle-fill" width={20} height={20} style={{ color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Failed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sync operations with errors
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label={dashboardData.syncOperations.failed} 
                      color="error" 
                      sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  </Box>
                </Stack>
              </StyledCard>
            </Grid>
          </Grid>
          */}

          {/* Upcoming Features Placeholder */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.03)
                      : theme.palette.grey[50]
                }}
              >
                <Icon
                  icon="eva:activity-fill"
                  width={48}
                  height={48}
                  style={{ color: theme.palette.text.secondary, marginBottom: '16px' }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                  Partner Status Monitor
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Real-time monitoring of partner connection status and health
                </Typography>
                <Chip 
                  label="Coming Soon" 
                  color="info" 
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </StyledCard>
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledCard
                sx={{
                  p: 4,
                  textAlign: 'center',
                  height: '100%',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.03)
                      : theme.palette.grey[50]
                }}
              >
                <Icon
                  icon="eva:sync-fill"
                  width={48}
                  height={48}
                  style={{ color: theme.palette.text.secondary, marginBottom: '16px' }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                  Sync Operations
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Track and manage data synchronization operations
                </Typography>
                <Chip 
                  label="Coming Soon" 
                  color="info" 
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </StyledCard>
            </Grid>
          </Grid>

          {/* TODO: Recent Activity Section (Coming Soon) */}
          {/*
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledCard sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#212b36' }}>
                    Recent Activity
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<Icon icon="eva:refresh-fill" />}>
                    Refresh
                  </Button>
                </Box>
                <Stack spacing={2}>
                  {dashboardData.recentActivity.map((activity) => (
                    <Box 
                      key={activity.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: `${getActivityColor(activity.status)}.lighter`, width: 32, height: 32 }}>
                          <Icon 
                            icon={getActivityIcon(activity.type)} 
                            width={16} 
                            height={16} 
                            style={{ color: 'white' }} 
                          />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activity.partner}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {activity.type === 'sync' ? 'Sync operation' : 
                             activity.type === 'error' ? 'Error occurred' : 
                             'Partner status update'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          label={activity.status} 
                          color={getActivityColor(activity.status)}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {activity.time}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </StyledCard>
            </Grid>
          </Grid>
          */}

          {/* Recent Activity - Coming Soon */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledCard
                sx={{
                  p: 4,
                  textAlign: 'center',
                  backgroundColor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.common.white, 0.03)
                      : theme.palette.grey[50]
                }}
              >
                <Icon
                  icon="eva:clock-fill"
                  width={48}
                  height={48}
                  style={{ color: theme.palette.text.secondary, marginBottom: '16px' }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                  Recent Activity
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  View and track recent partner activities and system events
                </Typography>
                <Chip 
                  label="Coming Soon" 
                  color="info" 
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </StyledCard>
            </Grid>
          </Grid>

          {/* Recent Activity Section */}
          {/* <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledCard sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#212b36' }}>
                    Recent Activity
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<Icon icon="eva:refresh-fill" />}>
                    Refresh
                  </Button>
                </Box>
                <Stack spacing={2}>
                  {dashboardData.recentActivity.map((activity) => (
                    <Box 
                      key={activity.id}
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        p: 2, 
                        bgcolor: 'grey.50', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'grey.200'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: `${getActivityColor(activity.status)}.lighter`, width: 32, height: 32 }}>
                          <Icon 
                            icon={getActivityIcon(activity.type)} 
                            width={16} 
                            height={16} 
                            style={{ color: 'white' }} 
                          />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {activity.partner}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {activity.type === 'sync' ? 'Sync operation' : 
                             activity.type === 'error' ? 'Error occurred' : 
                             'Partner status update'}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip 
                          label={activity.status} 
                          color={getActivityColor(activity.status)}
                          size="small"
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption" color="text.secondary" display="block">
                          {activity.time}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </StyledCard>
            </Grid>
          </Grid> */}
        </Box>
      </Container>
    </Page>
  );
};

export default OCPIDashboard;
